import type { Category, CategoryTreeNode } from '../types';

export function buildCategoryTree(rows: Category[]): CategoryTreeNode[] {
  const byParent = new Map<string | null, CategoryTreeNode[]>();
  const map = new Map<string, CategoryTreeNode>();

  for (const row of rows) {
    map.set(row.id, { ...row, children: [] });
  }

  for (const node of map.values()) {
    const parent = node.parentId;
    const list = byParent.get(parent) ?? [];
    list.push(node);
    byParent.set(parent, list);
  }

  const assignChildren = (node: CategoryTreeNode) => {
    const children = byParent.get(node.id) ?? [];
    children.sort((a, b) => a.order - b.order || a.name.localeCompare(b.name, 'tr'));
    node.children = children;
    children.forEach(assignChildren);
  };

  const roots = (byParent.get(null) ?? []).sort(
    (a, b) => a.order - b.order || a.name.localeCompare(b.name, 'tr'),
  );
  roots.forEach(assignChildren);
  return roots;
}

export function flattenTree(nodes: CategoryTreeNode[]): { id: string; depth: number; node: CategoryTreeNode }[] {
  const out: { id: string; depth: number; node: CategoryTreeNode }[] = [];
  const walk = (list: CategoryTreeNode[], depth: number) => {
    for (const n of list) {
      out.push({ id: n.id, depth, node: n });
      if (n.children.length) walk(n.children, depth + 1);
    }
  };
  walk(nodes, 0);
  return out;
}

export function getDescendantIds(rows: Category[], id: string): string[] {
  const childrenByParent = new Map<string, string[]>();
  for (const r of rows) {
    if (r.parentId) {
      const list = childrenByParent.get(r.parentId) ?? [];
      list.push(r.id);
      childrenByParent.set(r.parentId, list);
    }
  }
  const out: string[] = [];
  const walk = (rootId: string) => {
    const children = childrenByParent.get(rootId) ?? [];
    for (const c of children) {
      out.push(c);
      walk(c);
    }
  };
  walk(id);
  return out;
}
